// MIDAS V3 — Fullscreen artifact viewer (Phase 3)
//
// Spec: MIDAS_V3_PHASE3_ARTIFACT_DOCK_SPEC.md §9 + §10
// Handoff: artifact-compositions.jsx `FullscreenArtifact`.
//
// Rendered via React portal to document.body when
// `midasV3Slice.fullscreenArtifactId` is set. Covers the rail + chat + dock
// visually but the underlying DOM stays mounted so state survives unmount.
//
// Chrome:
//   • 48px top bar — back button + Esc chip + vertical divider + breadcrumb
//     + spacer + (i) describe + Stiahnuť ↓ download + Spýtať sa MIDAS CTA.
//   • Body — MidasArtifactPaneV3 mounted with fullscreen=true (the pane
//     suppresses its own header in that mode).
//
// Two exit paths (R2 sub-task 1 dropped the dock footer — fullscreen toggle
// now lives on the header which is also suppressed in fullscreen, so the
// in-overlay exits collapse to back-button + Esc):
//   1. Back button (top bar of this overlay) → setFullscreenArtifactId(null)
//   2. Esc key — owned by MidasV3Layout.tsx (single listener, dialog wins
//      priority over fullscreen so they don't fight).
//
// "+ Spýtať sa MIDAS" CTA exits fullscreen AND focuses the chat composer
// with prefilled text. The composer focus + prefill is implemented by
// exposing a global event (`midas-v3:focus-composer-with-text`) that
// ChatInputAreaV3 listens to — keeps coupling loose without lifting state
// into MidasV3Layout. Falls back gracefully when no composer is mounted.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
// R5 sub-task 3: add Eye/EyeOff (overlay toggle, diagram-only) and Shrink
// (right-corner exit-fullscreen). Shrink mirrors the dock header's
// open-fullscreen Expand icon (R5 sub-task 2c).
import { ArrowLeft, Download, Eye, EyeOff, Shrink } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import i18n from "../../../../../core/i18n";
import { sendMessage } from "../../../../../shared/chatBot/chatBotSlice";
import { DiagramOverlayArtifactData } from "../../../../../shared/chatBot/models/chatBotModels";
import { ArtifactTab } from "../../../midasPageModels";
import { machineArtifactDisplayTitle } from "./machineArtifactTitles";
import {
  setFullscreenArtifactId,
  setOverlayVisibility,
  MidasV3Theme,
} from "../../../midasV3Slice";
import MidasArtifactPaneV3 from "./MidasArtifactPaneV3";

// Event channel for composer-focus (also used by the CTA below).
export const MIDAS_V3_FOCUS_COMPOSER_EVENT = "midas-v3:focus-composer-with-text";

interface FocusComposerEventDetail {
  text: string;
}

export const dispatchFocusComposer = (text: string) => {
  if (typeof window === "undefined") return;
  const evt = new CustomEvent<FocusComposerEventDetail>(MIDAS_V3_FOCUS_COMPOSER_EVENT, {
    detail: { text },
  });
  window.dispatchEvent(evt);
};

// ───────────────────────────────────────────────────────────────────
// Helpers — describe payload mirrors v2 MC-26 / MC-27 (kept local to
// fullscreen so the top bar describe button works without the dock
// header being mounted).
// ───────────────────────────────────────────────────────────────────

const resolveInfoPrompt = (
  infoPrompt: { sk?: string; en?: string; cs?: string } | undefined,
  lang: string
): string | null => {
  if (!infoPrompt) return null;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  const primary = (infoPrompt as Record<string, string | undefined>)[langKey];
  if (primary && typeof primary === "string" && primary.length > 0) return primary;
  if (infoPrompt.sk && infoPrompt.sk.length > 0) return infoPrompt.sk;
  if (infoPrompt.en && infoPrompt.en.length > 0) return infoPrompt.en;
  if (infoPrompt.cs && infoPrompt.cs.length > 0) return infoPrompt.cs;
  for (const v of Object.values(infoPrompt)) {
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
};

const resolveLocalisedName = (
  data: DiagramOverlayArtifactData | undefined,
  lang: string
): string | undefined => {
  if (!data) return undefined;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en" && data.name_en) return data.name_en;
  if (langKey === "cs" && data.name_cs) return data.name_cs;
  if (data.name) return data.name;
  if (data.name_en) return data.name_en;
  if (data.name_cs) return data.name_cs;
  if (data.page_name) return data.page_name;
  return undefined;
};

const synthesiseInfoPrompt = (tab: ArtifactTab, lang: string): string | null => {
  if (tab.artifact.type !== "diagram_overlay") return null;
  const data = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = data?.page ?? tab.artifact.page;
  if (page === undefined || page === null) return null;
  const name = resolveLocalisedName(data, lang) ?? tab.artifact.title;
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en") {
    return name
      ? `Explain page ${page} — ${name} from the electrical schema collection`
      : `Explain page ${page} from the electrical schema collection`;
  }
  return name
    ? `Vysvetli mi stránku ${page} — ${name} zo zbierky elektrických schém`
    : `Vysvetli mi stránku ${page} zo zbierky elektrických schém`;
};

const buildFriendlyDisplayText = (tab: ArtifactTab, lang: string): string => {
  const data = tab.artifact.data as DiagramOverlayArtifactData | undefined;
  const page = data?.page ?? tab.artifact.page;
  const name = resolveLocalisedName(data, lang);
  const langKey = (lang || "").toLowerCase().split("-")[0];
  if (langKey === "en") {
    return name ? `Describe page ${page} — ${name}` : `Describe page ${page}`;
  }
  return name ? `Popis stránky ${page} — ${name}` : `Popis stránky ${page}`;
};

// ───────────────────────────────────────────────────────────────────
// FullscreenArtifactV3
// ───────────────────────────────────────────────────────────────────

const FullscreenArtifactV3: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("chatBot");
  // ME-CARDS-R1 A1: evidence-card display labels live in the machineEvidence ns.
  const { translate: translateEvidence } = useTranslation("machineEvidence");

  const fullscreenArtifactId = useAppSelector(
    (s) => s.midasV3.fullscreenArtifactId
  );
  const tabs = useAppSelector((s) => s.midasPage.artifactTabs);
  const activeTab = tabs.find((t) => t.id === fullscreenArtifactId) ?? null;

  const theme = useAppSelector((s) => s.midasV3.theme) as MidasV3Theme;
  const activeThreadId = useAppSelector((s) => s.midasPage.activeThreadId);
  const machineContext = useAppSelector((s) => s.chatBot.machineContext);

  const isStreamingGlobal = useAppSelector((state) => {
    if (state.chatBot.isLoading) return true;
    const buffers = state.chatBot.threadBuffers || {};
    for (const key of Object.keys(buffers)) {
      if (buffers[key]?.isLoading) return true;
    }
    return false;
  });

  const handleExit = useCallback(() => {
    dispatch(setFullscreenArtifactId(null));
  }, [dispatch]);

  // R5 sub-task 3: overlay-eye toggle in the fullscreen top bar mirrors the
  // dock header pattern (ArtifactHeaderV3 R3 sub-task 1). Diagram-only —
  // gated below on activeTab.artifact.type === "diagram_overlay". Reads +
  // writes the SAME slice field the dock eye uses, so the two stay in sync
  // when the user transitions between dock and fullscreen.
  const isDiagramOverlay = activeTab?.artifact.type === "diagram_overlay";
  const overlaysVisible = useAppSelector((s) => {
    if (!fullscreenArtifactId) return true;
    const stored = s.midasV3.overlayVisibility[fullscreenArtifactId];
    return stored === undefined ? true : stored;
  });
  const handleEyeToggle = useCallback(() => {
    if (!fullscreenArtifactId) return;
    dispatch(
      setOverlayVisibility({
        artifactId: fullscreenArtifactId,
        visible: !overlaysVisible,
      })
    );
  }, [fullscreenArtifactId, overlaysVisible, dispatch]);

  const handleDescribe = useCallback(() => {
    if (!activeTab) return;
    if (isStreamingGlobal) return;
    const data = activeTab.artifact.data as DiagramOverlayArtifactData | undefined;
    const fromBot = resolveInfoPrompt(data?.info_prompt, i18n.language);
    const promptBody = fromBot ?? synthesiseInfoPrompt(activeTab, i18n.language);
    if (!promptBody) return;
    const friendly = buildFriendlyDisplayText(activeTab, i18n.language);

    let pageInfoRequest:
      | { page: number; collection: string; list_id?: string }
      | undefined;
    if (activeTab.artifact.type === "diagram_overlay") {
      const page = data?.page ?? activeTab.artifact.page;
      if (typeof page === "number" && Number.isFinite(page)) {
        pageInfoRequest = {
          page,
          collection: data?.collection ?? "sbl500_electrical_toc_v2",
          ...(data?.list_id ? { list_id: data.list_id } : {}),
        };
      }
    }

    dispatch(
      sendMessage({
        message: promptBody,
        displayText: friendly,
        machineId: machineContext?.machineId,
        threadId: activeThreadId || undefined,
        originThreadId: activeThreadId || undefined,
        pageInfoRequest,
      })
    );
  }, [activeTab, isStreamingGlobal, machineContext, activeThreadId, dispatch]);

  // Download — wire to artifact's known URL when present. Best-effort: opens
  // the URL in a new tab so the browser handles the actual download. For
  // bot-proxied artifacts the URL is a relative path under /v1/bot/... which
  // resolves correctly against the SPA host.
  const handleDownload = useCallback(() => {
    if (!activeTab) return;
    const url = activeTab.artifact.url;
    if (!url) return;
    // Heuristic: drive same-tab navigation only if it's clearly a file (has
    // an extension); otherwise open a new tab so the user doesn't lose the
    // chat surface.
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = activeTab.artifact.title || "artifact";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [activeTab]);

  const handleAskMidas = useCallback(() => {
    if (!activeTab) {
      handleExit();
      return;
    }
    // ME-CARDS-R1 A1: ask Midas about the card by its DISPLAY name, never by a
    // GUID title (the prefix is typed into the composer for the user to see).
    const name = machineArtifactDisplayTitle(activeTab.artifact, translateEvidence) || "";
    // USER-LANG-IT-PL-1 L3-FE: was an EN-vs-SK literal (cs got Slovak, any new language
    // would too); now a chatBot key so every locale carries its own prefix.
    const prefix = translate("midas.v3.artifact.askAboutPrefix", {
      name,
      defaultValue: "Asking about {{name}} — ",
    }) as string;
    handleExit();
    // After unmount lets the layout switch back, the next tick gives
    // ChatInputAreaV3 a chance to mount/be live.
    requestAnimationFrame(() => dispatchFocusComposer(prefix));
  }, [activeTab, handleExit, translate, translateEvidence]);

  // Build breadcrumb from machine context + artifact metadata.
  const breadcrumb = useMemo(() => {
    const machineName = machineContext?.model
      ? `${machineContext.manufacturerName ?? ""} ${machineContext.model}`.trim()
      : translate("midas.v3.artifact.breadcrumbHome", {
          defaultValue: "Midas",
        }) as string;
    const section = activeTab
      ? translate(
          `midas.v3.artifact.section.${activeTab.artifact.type}` as never,
          {
            defaultValue:
              activeTab.artifact.type === "diagram_overlay"
                ? (translate("midas.v3.artifact.section.diagram_overlay", {
                    defaultValue: "Schémy",
                  }) as string)
                : (activeTab.artifact.type as string),
          }
        ) as string
      : "";
    return {
      machineName,
      section,
      // ME-CARDS-R1 A1: display label, not the raw (possibly GUID) bot title.
      title: activeTab ? machineArtifactDisplayTitle(activeTab.artifact, translateEvidence) : "",
    };
  }, [machineContext, activeTab, translate, translateEvidence]);

  if (!fullscreenArtifactId || !activeTab) return null;
  if (typeof document === "undefined") return null;

  const backLabel = (translate("midas.v3.artifact.backToChat", {
    defaultValue: "Späť do chatu",
  }) as string);

  const downloadLabel = (translate("midas.v3.artifact.download", {
    defaultValue: "Stiahnuť ↓",
  }) as string);

  const askLabel = (translate("midas.v3.artifact.askMidas", {
    defaultValue: "+ Spýtať sa MIDAS",
  }) as string);

  const overlayNode = (
    <div
      className={`midas-v3-root p-${theme} midas-v3-fullscreen-overlay`}
      role="dialog"
      aria-modal="true"
      aria-label={(translate("midas.v3.artifact.fullscreenAria", {
        defaultValue: "Artifact fullscreen view",
      }) as string)}
      style={{
        position: "fixed",
        inset: 0,
        // MIDAS-V3-BUGS-0521 root-cause + fix:
        // The MUI AppBar's parent in the app shell creates a stacking
        // context at z-index > 1200 (the static-positioned <header>
        // showed computed zIndex:1250 in DevTools). A `position:fixed`
        // overlay at zIndex:1200 paints in its OWN stacking context,
        // which loses against the AppBar's higher-z sibling stacking
        // context — so the AppBar painted on top of the overlay,
        // hiding everything (including the 48px top bar with the
        // back/eye/info/download/Ask-Midas/Shrink controls). No amount
        // of nested z-index inside the overlay can win across a
        // sibling stacking context boundary; only bumping the OVERLAY
        // root works. 2000 sits above MUI tooltip (1500) and Snackbar
        // (1400) with headroom but isn't absurd. Live-confirmed in
        // DevTools by editing this value on the deployed bundle.
        zIndex: 2000,
        background: "var(--bg)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Top bar */}
      <div
        style={{
          height: 48,
          flexShrink: 0,
          padding: "0 20px",
          background: "var(--card)",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <button
          type="button"
          onClick={handleExit}
          aria-label={backLabel}
          title={backLabel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "0 10px",
            background: "transparent",
            border: "1px solid transparent",
            borderRadius: 8,
            cursor: "pointer",
            color: "var(--ink-2)",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            transition: "background 0.12s ease, color 0.12s ease",
          }}
        >
          <ArrowLeft size={16} strokeWidth={2} />
          <span>{backLabel}</span>
        </button>

        {/* Esc hint chip */}
        <span
          className="mono"
          title={
            translate("midas.v3.artifact.escHint", {
              defaultValue: "Press Esc to exit fullscreen",
            }) as string
          }
          style={{
            fontSize: 10.5,
            color: "var(--muted)",
            padding: "2px 7px",
            border: "1px solid var(--border)",
            borderRadius: 4,
            background: "var(--bg-soft)",
            letterSpacing: "0.04em",
            fontFamily: "var(--font-mono)",
          }}
        >
          Esc
        </span>

        <span
          aria-hidden="true"
          style={{ height: 18, width: 1, background: "var(--border)" }}
        />

        {/* Breadcrumb */}
        <div
          style={{
            fontSize: 12,
            color: "var(--muted)",
            display: "flex",
            alignItems: "center",
            gap: 0,
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            flexShrink: 1,
          }}
        >
          <span
            className="mono"
            style={{
              color: "var(--accent)",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              marginRight: 4,
            }}
          >
            ◆
          </span>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 220,
            }}
          >
            {breadcrumb.machineName}
          </span>
          <span style={{ color: "var(--faint)", margin: "0 4px" }}> · </span>
          <span>{breadcrumb.section}</span>
          <span style={{ color: "var(--faint)", margin: "0 4px" }}> · </span>
          <span
            style={{
              color: "var(--ink-2)",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 320,
            }}
          >
            {breadcrumb.title}
          </span>
        </div>

        <span style={{ flex: 1 }} aria-hidden="true" />

        {/* R5 sub-task 3: eye-toggle restored to fullscreen top bar.
            Diagram-only — same dispatch + same slice key the dock header
            uses, so dock ↔ fullscreen transitions keep overlay state. */}
        {isDiagramOverlay && (
          <button
            type="button"
            onClick={handleEyeToggle}
            aria-label={
              (overlaysVisible
                ? translate("midas.v3.artifact.hideOverlays", {
                    defaultValue: "Hide overlays",
                  })
                : translate("midas.v3.artifact.showOverlays", {
                    defaultValue: "Show overlays",
                  })) as string
            }
            title={
              (overlaysVisible
                ? translate("midas.v3.artifact.hideOverlays", {
                    defaultValue: "Hide overlays",
                  })
                : translate("midas.v3.artifact.showOverlays", {
                    defaultValue: "Show overlays",
                  })) as string
            }
            style={{
              width: 32,
              height: 32,
              padding: 0,
              border: "none",
              background: "transparent",
              borderRadius: 8,
              cursor: "pointer",
              display: "inline-grid",
              placeItems: "center",
              color: overlaysVisible ? "var(--accent)" : "var(--muted)",
              transition: "color 0.12s ease, background 0.12s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--card-2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {overlaysVisible ? (
              <Eye size={16} strokeWidth={2} />
            ) : (
              <EyeOff size={16} strokeWidth={2} />
            )}
          </button>
        )}

        {/* Describe (i) */}
        <button
          type="button"
          onClick={handleDescribe}
          disabled={isStreamingGlobal}
          aria-label={(translate("midas.v3.artifact.describeButton.tooltip", {
            defaultValue: "Describe this page",
          }) as string)}
          title={(translate("midas.v3.artifact.describeButton.tooltip", {
            defaultValue: "Describe this page",
          }) as string)}
          style={{
            width: 32,
            height: 32,
            padding: 0,
            border: "none",
            background: "transparent",
            borderRadius: 999,
            cursor: isStreamingGlobal ? "not-allowed" : "pointer",
            display: "inline-grid",
            placeItems: "center",
            opacity: isStreamingGlobal ? 0.55 : 1,
            flexShrink: 0,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-grid",
              placeItems: "center",
              width: 18,
              height: 18,
              borderRadius: 999,
              border: "1.5px solid var(--accent)",
              color: "var(--accent)",
              fontFamily: "var(--font-sans)",
              fontWeight: 700,
              fontSize: 11,
              fontStyle: "italic",
              background: "var(--accent-soft)",
              lineHeight: 1,
            }}
          >
            i
          </span>
        </button>

        <button
          type="button"
          onClick={handleDownload}
          aria-label={downloadLabel}
          title={downloadLabel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "0 12px",
            background: "var(--card-2)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            cursor: "pointer",
            color: "var(--ink-2)",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          <Download size={14} strokeWidth={2} />
          <span>{downloadLabel}</span>
        </button>

        <button
          type="button"
          onClick={handleAskMidas}
          aria-label={askLabel}
          title={askLabel}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            padding: "0 14px",
            background: "var(--accent)",
            color: "white",
            border: "1px solid var(--accent)",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            fontWeight: 600,
            flexShrink: 0,
            boxShadow: "var(--shadow)",
          }}
        >
          {askLabel}
        </button>

        {/* R5 sub-task 3: right-corner exit-fullscreen icon. Mirrors the
            dock header's open-fullscreen Expand icon (R5 sub-task 2c) so
            the open/exit pair is visually obvious. The left back-button +
            Esc-chip stay as secondary exit paths (some users rely on
            keyboard, others scan top-right for the toggle). */}
        <button
          type="button"
          onClick={handleExit}
          aria-label={
            (translate("midas.v3.artifact.exitFullscreenButton.tooltip", {
              defaultValue: "Exit fullscreen",
            }) as string)
          }
          title={
            (translate("midas.v3.artifact.exitFullscreenButton.tooltip", {
              defaultValue: "Exit fullscreen",
            }) as string)
          }
          style={{
            width: 32,
            height: 32,
            padding: 0,
            border: "none",
            background: "transparent",
            borderRadius: 8,
            cursor: "pointer",
            display: "inline-grid",
            placeItems: "center",
            color: "var(--ink-2)",
            transition: "color 0.12s ease, background 0.12s ease",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--card-2)";
            e.currentTarget.style.color = "var(--ink)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--ink-2)";
          }}
        >
          <Shrink size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Body — the artifact viewer fills the remaining space. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
        }}
      >
        <MidasArtifactPaneV3 fullscreen />
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlayNode, document.body);
};

// Listen for the focus-composer event and wire it from inside ChatInputAreaV3
// (so this module can stay decoupled). For test convenience, expose a
// non-React util that runs the same focusing.
export const useFocusComposerEvent = (
  onFocus: (text: string) => void
): void => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<FocusComposerEventDetail>).detail;
      if (detail && typeof detail.text === "string") {
        onFocus(detail.text);
      }
    };
    window.addEventListener(MIDAS_V3_FOCUS_COMPOSER_EVENT, handler);
    return () =>
      window.removeEventListener(MIDAS_V3_FOCUS_COMPOSER_EVENT, handler);
  }, [onFocus]);
};

export default FullscreenArtifactV3;
